import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from '../axiosInstance';
import { Calendar, Layers, MapPin, Package, Clipboard, ChevronDown, ChevronUp } from 'lucide-react';

export default function ScheduleAuctionList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [records, setRecords] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);

  const fetchSchedules = async (page) => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`schedule-auction/list?page=${page}&limit=${limit}`);
      setRecords(res.data.data || []);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error('Error fetching schedules:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load scheduled auctions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules(currentPage);
  }, [currentPage]);

  const toggleRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-8 text-gray-900">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl relative mb-6">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-blue-800 tracking-tight flex items-center gap-3">
            <Calendar className="text-blue-600 w-8 h-8 md:w-10 md:h-10" />
            Scheduled Auctions
          </h1>
          <p className="text-gray-600 mt-1 text-sm md:text-base">
            Manage and view all scheduled auctions and their associated warehouse lots.
          </p>
        </div>
        <button
          onClick={() => navigate('/schedule-auction/create')}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 font-semibold w-full sm:w-auto text-center"
        >
          + Schedule Auction
        </button>
      </div>

      {/* Table Section */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="overflow-hidden rounded-2xl shadow-xl border border-gray-100 bg-white"
      >
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[800px]">
              <thead className="bg-gray-100 border-b border-gray-200">
                <tr>
                  <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase">#</th>
                  <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase">Auction Date</th>
                  <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase">State</th>
                  <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase">Commodity</th>
                  <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase">Season</th>
                  <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase">Scheme</th>
                  <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase">Status</th>
                  <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase">Warehouses</th>
                  <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase text-center">Details</th>
                </tr>
              </thead>
              <tbody>
                {records.map((item, idx) => {
                  const isExpanded = !!expandedRows[item.id];
                  return (
                    <React.Fragment key={item.id}>
                      <tr className="hover:bg-gray-50 border-b border-gray-100 transition-colors duration-150">
                        <td className="py-4 px-6 text-sm text-gray-700 font-medium">
                          {idx + 1 + (currentPage - 1) * limit}
                        </td>
                        <td className="py-4 px-6 text-sm font-semibold text-gray-900">
                          {new Date(item.auction_date).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-800">
                          <span className="flex items-center gap-1">
                            <MapPin size={15} className="text-gray-400" />
                            {item.state?.name || 'N/A'}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-800">
                          <span className="flex items-center gap-1">
                            <Package size={15} className="text-gray-400" />
                            {item.commodity?.commodityname || 'N/A'}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-800">
                          <span className="flex items-center gap-1">
                            <Layers size={15} className="text-gray-400" />
                            {item.season?.name || 'N/A'}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-800">
                          <span className="flex items-center gap-1">
                            <Layers size={15} className="text-gray-400" />
                            {item.scheme?.name || 'N/A'}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-sm">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${item.status === 'PROCESSED'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                            }`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-700 font-medium">
                          {item.warehouses?.length || 0} selected
                        </td>
                        <td className="py-4 px-6 text-sm text-center">
                          <button
                            onClick={() => toggleRow(item.id)}
                            className="text-blue-600 hover:text-blue-800 focus:outline-none transition-colors p-1"
                          >
                            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-blue-50/40">
                          <td colSpan="9" className="px-8 py-4 border-b border-gray-200">
                            <div className="rounded-xl bg-white border border-blue-100 shadow-sm p-4">
                              <h4 className="text-xs font-bold text-blue-800 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                <Clipboard size={14} /> Warehouse Details and Allocations
                              </h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                {item.warehouses?.map((wMapping) => (
                                  <div
                                    key={wMapping.id}
                                    className="p-3 rounded-lg border border-gray-100 bg-gray-50 flex flex-col justify-between"
                                  >
                                    <div>
                                      <p className="font-semibold text-gray-900 text-sm">
                                        {wMapping.warehouse?.warehouse_name}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-0.5">
                                        Code: {wMapping.warehouse?.warehouse_code}
                                      </p>
                                    </div>
                                    <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-200/60">
                                      <div>
                                        <span className="text-[10px] uppercase font-bold text-gray-400">Qty (MT)</span>
                                        <p className="font-bold text-xs text-blue-700">{wMapping.current_quantity}</p>
                                      </div>
                                      <div className="text-right">
                                        <span className="text-[10px] uppercase font-bold text-gray-400">Bags</span>
                                        <p className="font-bold text-xs text-gray-700">{wMapping.current_bags}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {records.length === 0 && !loading && (
                  <tr>
                    <td colSpan="9" className="text-center py-12 text-gray-500 font-medium text-lg">
                      🚫 No scheduled auctions found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6">
          <button
            disabled={currentPage === 1 || loading}
            onClick={() => setCurrentPage(prev => prev - 1)}
            className="px-4 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-40 rounded-lg font-semibold transition"
          >
            Previous
          </button>
          <span className="text-gray-700 font-semibold">
            Page {currentPage} of {totalPages}
          </span>
          <button
            disabled={currentPage === totalPages || loading}
            onClick={() => setCurrentPage(prev => prev + 1)}
            className="px-4 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-40 rounded-lg font-semibold transition"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
